import type { FC } from 'react';

import { useContent } from '@eneco/cms';
import { Shadow } from '@eneco/custom-components/Registration';
import useDC from '@eneco/dc/useDC';
import { Box, EnecoLogo, NavLink, OxxioLogo, PageGrid, Stack, WoonenergieLogo } from '@eneco/sparky';
import type { DC_Repositories_Base_Enumerations_Label } from '@monorepo-types/dc';

import type { RegistrationHeaderContent } from './registration-header-content.types';

const getLogo = (label: DC_Repositories_Base_Enumerations_Label) => {
  switch (label) {
    case 'oxxio':
      return OxxioLogo;
    case 'woonenergie':
      return WoonenergieLogo;
    case 'eneco':
    default:
      return EnecoLogo;
  }
};

/**
 * `RegistrationHeader` block for the CMS-agnostic lane, serving **both** providers.
 *
 * Mirrors the legacy component (`apps/accessmanagement/nl/multilabel/src/components/RegistrationHeader/
 * RegistrationHeader.tsx`): the label's logo and a customer service link on the primary background,
 * under a shadow. The logo follows the Digital Core label, not content. It has no settings and no
 * nested placeholders.
 */
const RegistrationHeader: FC = () => {
  const { label } = useDC();
  const content = useContent<RegistrationHeaderContent>();
  const Logo = getLogo(label);

  return (
    <Shadow>
      <Box backgroundColor="backgroundPrimary" paddingY="3">
        <PageGrid>
          <PageGrid.Item gridColumn="1/-1">
            <Stack direction="row" alignX="justify" alignY="center">
              <Logo />
              <Stack direction="row" gap="4">
                <NavLink href={content.customerServiceLink?.href} variant="tertiary">
                  {content.customerServiceLink?.text}
                </NavLink>
              </Stack>
            </Stack>
          </PageGrid.Item>
        </PageGrid>
      </Box>
    </Shadow>
  );
};

export default RegistrationHeader;
